import React, {useState} from "react";
import {useCollapsibleRows} from "@vigo/hooks";
import {Card, Tooltip, Row} from "antd";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {BackOfficePaidByBothCashAndCardTabFilters} from "./BackofficePaidByBothCashAndCardTabFilters";
import moment from "moment";
import {codPaymentStatuses, codPaymentTypes} from "../../../../constants/constants";
import {isConstantExist} from "../../../../utils/helpers";
import {useTranslation} from "react-i18next";
import {ExpandableStyledTable} from "components/_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
const PAGE_SIZE = 10;
const formatDate = date => {
    return moment(date).format("DD.MM.YYYY HH:mm");
};
export const BackOfficePaidByBothCashAndCardTab = ({isTabOpened = false}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const expandableState = useCollapsibleRows({isTabOpened, page});
    const [setSelectedRowKeys] = useState([]);
    const [recall] = useState(false);

    const [loading, payments] = useFetch({
        fetchFn: stores.codPaymentStore.getPaidByBothCashAndCardPayments.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: !stores.meStore.checkRole("isCompanyUser") && isTabOpened
    });

    const expandedRowRender = record => {
        const columns = [
            {
                title: "Transaction Id",
                dataIndex: "transactionId",
                key: "transactionId",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: text => <Tooltip title={text}>{text}</Tooltip>,
                onCell: () => ({
                    "data-label": "Transaction Id"
                })
            },
            {
                title: "Ödeme Tutarı",
                dataIndex: "transactionAmount",
                key: "amount",
                width: 100,
                render: text => text && `${text} TL`,
                onCell: () => ({
                    "data-label": "Ödeme Tutarı"
                })
            },
            {
                title: "Ödeme yöntemi",
                dataIndex: "paymentType",
                key: "paymentMethod",
                width: 120,
                render: paymentType =>
                    `${
                        isConstantExist(codPaymentTypes, paymentType)
                            ? t(`paymentTypes.${paymentType}`, {
                                  ns: "codPayment"
                              })
                            : paymentType
                    }`,
                onCell: () => ({
                    "data-label": "Ödeme Yöntemi"
                })
            },
            {
                title: "İşlem Tarihi",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 150,
                render: text => new Date(text).toLocaleString("tr-TR"),
                onCell: () => ({
                    "data-label": "İşlem Tarihi"
                })
            }
        ];

        return (
            <ResponsiveDataTable
                columns={columns}
                dataSource={record.transactions || []}
                pagination={false}
                rowKey="_id"
                size="small"
            />
        );
    };

    const columns = [
        {
            title: "Firma",
            dataIndex: ["company", "companyName"],
            key: "company",
            render: (text, row) => (
                <Link
                    data-testid="companyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/" + row?.company?._id
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Firma"
            })
        },
        {
            title: "Alt Firma",
            dataIndex: ["subCompany", "name"],
            key: "subCompany",
            render: (text, row) => (
                <Link
                    data-testid="subCompanyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/sub-company/" + row?.subCompany?._id
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Alt Firma"
            })
        },
        {
            title: "Ödeme Id",
            dataIndex: "_id",
            key: "_id",
            render: text => text,
            onCell: () => ({
                "data-label": "Ödeme Id"
            })
        },
        {
            title: "Tutar",
            dataIndex: ["payment", "receivedAmount"],
            key: "amount",
            render: text => text && `${text} TL`,
            onCell: () => ({
                "data-label": "Tutar"
            })
        },
        {
            title: "İşlem Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: text => text && formatDate(text),
            onCell: () => ({
                "data-label": "İşlem Tarihi"
            })
        },
        {
            title: "Ödeme Yöntemi",
            dataIndex: ["payment", "paymentType"],
            key: "paymentMethod",
            render: paymentType =>
                `${
                    isConstantExist(codPaymentTypes, paymentType)
                        ? t(`paymentTypes.${paymentType}`, {
                              ns: "codPayment"
                          })
                        : paymentType
                }`,
            onCell: () => ({
                "data-label": "Ödeme Yöntemi"
            })
        },
        {
            title: "İşlem Durumu",
            dataIndex: ["status"],
            key: "status",
            render: text => {
                const status = codPaymentStatuses?.find(status => status?.value === text);
                return status?.name || text;
            },
            onCell: () => ({
                "data-label": "İşlem Durumu"
            })
        },
        {
            title: "Sipariş No",
            dataIndex: ["delivery", "vigoOrderNumber"],
            key: "vigoOrderNumber",
            render: (text, row) => (
                <Link
                    data-testid="vigoOrderNumber"
                    target="_blank"
                    to={{
                        pathname: "/orders/" + row?.deliveryInfo?._id
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Sipariş No"
            })
        },
        {
            title: "Kurye",
            dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
            key: "courier",
            render: (text, row) => (
                <Link
                    data-testid="courierName"
                    target="_blank"
                    to={{
                        pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                    }}
                >
                    <>{text}</>
                </Link>
            ),
            onCell: () => ({
                "data-label": "Kurye"
            })
        }
    ];

    return (
        <Card>
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <BackOfficePaidByBothCashAndCardTabFilters
                    filters={filters}
                    setFilters={setFilters}
                />
            </Row>
            <ExpandableStyledTable
                loading={loading}
                columns={columns}
                size="small"
                dataSource={payments?.rows || []}
                rowKey="_id"
                scroll={{x: "max-content"}}
                expandable={{
                    ...expandableState,
                    expandedRowRender
                }}
                pagination={{
                    total: payments?.dataCount || 0,
                    current: page,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: PAGE_SIZE,
                    onChange: page => {
                        setPage(page);
                        setSelectedRowKeys([]);
                    }
                }}
            />
        </Card>
    );
};
